import React, { useRef } from "react";
import { BasicIcon, Button, Tooltip, ToolTipPosition } from "@pickit/pickit-components";
import { useTranslation } from "react-i18next";
import PdfController from "component/pdfController/PdfController";
import { PdfControllerRef } from "component/pdfController/types";
import { PdfViewer } from "component/pdf-viewer/PdfViewer";
import "./documentManager.scss";
import { PdfUploadCardProps } from "./types";
import classNames from "classnames";

export const PdfUploadCard: React.FC<PdfUploadCardProps> = ({
  testId,
  title,
  tooltipMessage,
  fileLabel,
  loadButtonText,
  updateButtonLabel,
  currentFile,
  fileStatus,
  fileHandler,
  goToPreviousFile,
  onDeleteFile,
  onUpdate,
  onFileTypeError,
}) => {
  const { t } = useTranslation();
  const pdfControllerRef = useRef<PdfControllerRef>(null);

  return (
    <div className="doc-manager-upload-card">
      <div className="doc-manager-card-header">
        <h2 className="doc-manager-section-title" data-testId={`${testId}-update-file-title`}>
          {title}
          {tooltipMessage && (
            <Tooltip message={tooltipMessage} position={ToolTipPosition.bottomRight}>
              <BasicIcon
                name="information-circle"
                size="small"
                color="#0098ff"
                data-testId={`${testId}-tooltip-info`}
              />
            </Tooltip>
          )}
        </h2>
      </div>
      <div className="doc-manager-upload-body">
        <PdfController
          ref={pdfControllerRef}
          processAllPages={true}
          title={fileLabel}
          buttonText={loadButtonText}
          fileUploaded={!!currentFile?.url}
          showError={fileStatus.error}
          errorMessage={fileStatus?.message || ""}
          loading={fileStatus.loading}
          fileHandler={fileHandler}
          disabled={fileStatus.loading}
          goToPreviousFile={goToPreviousFile}
          accept=".pdf"
          onFileTypeError={onFileTypeError}
        >
          {currentFile?.url && (
            <PdfViewer src={currentFile.url}>
              <Tooltip
                disabled={!currentFile?.url}
                message={t("global:delete")}
                position={ToolTipPosition.top}
              >
                <div className={classNames("icon-container-primary", { "disabled": fileStatus.loading })}>
                <BasicIcon onClick={() => onDeleteFile?.()} data-testId={`${testId}-delete-pdf-button`} name="trash" size="small" />
                </div>
              </Tooltip>
              <div style={{ display: "none" }}>
                <Tooltip
                  disabled={!currentFile?.url}
                  message={t("global:replace")}
                  position={ToolTipPosition.top}
                >
                  <div className={classNames("icon-container-primary", { "disabled": fileStatus.loading })}>
                  <BasicIcon onClick={() => pdfControllerRef.current?.triggerOnChange()} data-testId={`${testId}-replace-pdf-button`} name="switch-vertical" size="small" />
                  </div>
                  </Tooltip>
              </div>
            </PdfViewer>
          )}
        </PdfController>
        <div className="doc-manager-button-container">
          <Button
            disabled={!currentFile?.url || fileStatus.loading}
            testId={`${testId}-update-button`}
            label={updateButtonLabel}
            onClick={onUpdate}
          />
        </div>
      </div>
    </div>
  );
};

export default PdfUploadCard;
